import { getEProvinceList } from '@if/core/types'
import { Grid } from '@mui/material'
import { useMemo } from 'react'
import { Control, Controller, FieldError, FieldErrorsImpl, Merge, UseFormRegister } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { InputTextField, InputTextFieldWithMask, SelectComponent } from '../../../../../../../components'
import { Address, EMask } from '../../../../../../../data/types'

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>
  errors?: Merge<FieldError, FieldErrorsImpl<Address>>
  name: string
  isCommercial?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formControl: Control<any>
  includeDuration?: boolean
  editDisabled: boolean
}

const AddressComponent = ({
  register,
  errors,
  name,
  isCommercial,
  formControl,
  includeDuration = true,
  editDisabled,
}: Props) => {
  const { t } = useTranslation()

  const provinceOptions = useMemo(
    () =>
      getEProvinceList()
        .sort()
        .map((item) => ({ label: t(`EProvince.${item}`, { ns: 'enum' }), value: item })),
    [t],
  )

  return (
    <Grid container spacing={2} alignContent="space-between" size={12}>
      <input type="hidden" {...register(`${name}.country`)} value="Canada" />
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <InputTextField
          error={errors?.civicNumber}
          label={t('editCreditApplication.address.addressNumber')}
          {...register(`${name}.civicNumber`)}
          maxLength={10}
          disabled={editDisabled}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <InputTextField
          error={errors?.street}
          label={t('editCreditApplication.address.street')}
          {...register(`${name}.street`)}
          maxLength={100}
          disabled={editDisabled}
        />
      </Grid>
      {!isCommercial && (
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField
            error={errors?.apartment}
            label={t('editCreditApplication.address.apartment')}
            {...register(`${name}.apartment`)}
            maxLength={50}
            disabled={editDisabled}
          />
        </Grid>
      )}
      {isCommercial && (
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField
            error={errors?.suite}
            label={t('editCreditApplication.address.suite')}
            {...register(`${name}.suite`)}
            maxLength={10}
            disabled={editDisabled}
          />
        </Grid>
      )}
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <InputTextField
          error={errors?.city}
          label={t('editCreditApplication.address.city')}
          {...register(`${name}.city`)}
          maxLength={50}
          disabled={editDisabled}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Controller
          name={`${name}.postalCode`}
          control={formControl}
          render={({ field }) => (
            <InputTextFieldWithMask
              mask={EMask.postalCode}
              error={errors?.postalCode}
              label={t('editCreditApplication.address.postalCode')}
              {...field}
              disabled={editDisabled}
            />
          )}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <SelectComponent
          items={provinceOptions}
          label={t('editCreditApplication.address.province')}
          {...register(`${name}.stateIso`)}
          error={errors?.stateIso}
          disabled={editDisabled}
        />
      </Grid>
      {includeDuration && (
        <>
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
          >
            <InputTextField
              error={errors?.years}
              label={t('editCreditApplication.address.since.years')}
              {...register(`${name}.years`)}
              disabled={editDisabled}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
          >
            <InputTextField
              error={errors?.months}
              label={t('editCreditApplication.address.since.months')}
              {...register(`${name}.months`)}
              disabled={editDisabled}
            />
          </Grid>
        </>
      )}
    </Grid>
  )
}

export default AddressComponent
